import React, { useContext } from 'react'
import { Style } from '@react-pdf/types/style'
import { generatePrefix, ListContext, OrderTypes } from './MarkdownList'
import { MarkdownContext } from './types'
import { MarkdownDropdownSelectComponent } from '../../cloze-dropdown/helpers'

// ---------------------------------------------------------------------- Basic Tag Overrides ---------------------------------------------------------------

/**
 * ViewWrapper - a component which wraps the given children in a view component
 *
 * @param {Props} props - the react props for the component
 * @param {React.ReactChild | React.ReactChild[]} props.children - the children to wrap with a view component
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 * @param {?Style} props.style - the styles to apply to the children
 *
 * @return {JSX.Element} - the children wrapped in a View component
 */
export function ViewWrapper({
	children,
	markdownContext,
	style,
}: {
	children: React.ReactChild | React.ReactChild[]
	markdownContext: MarkdownContext
	style?: Style
}): JSX.Element {
	const { View } = markdownContext.ReactPdf
	return (
		<View>
			<CollapseText style={style} markdownContext={markdownContext}>
				{children}
			</CollapseText>
		</View>
	)
}

/**
 * List - a pdf component which replaces the <ul> and <ol> tags
 *
 * @param {props} props - the react props for the component
 * @param {React.ReactChild | React.ReactChild[]} props.children - the elements in the list
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 * @param {OrderTypes} props.type - weather to display an ordered or unordered list
 * @param {number} props.start - the starting number of the list
 *
 * @return {JSX.Element} - a component which can render the list to a pdf
 */
export function List({
	children,
	markdownContext,
	type,
	start,
}: {
	children: React.ReactChild | React.ReactChild[]
	markdownContext: MarkdownContext
	type: OrderTypes
	start: number
}): JSX.Element {
	const { View } = markdownContext.ReactPdf
	const parentContext = useContext(ListContext)
	return (
		<ListContext.Provider
			value={{
				type,
				level: parentContext.type === type ? parentContext.level + 1 : 0,
				value: { current: start },
			}}>
			<View style={markdownContext.pdfStyles.list}>{children}</View>
		</ListContext.Provider>
	)
}

/**
 * ListItem - display a list item in the pdf (replaces the <li> tag)
 *
 * @param {{children: React.ReactChild | React.ReactChild[]}} {children} - the children for the list item
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element} - an element which displays the list item in the pdf
 */
export function ListItem({
	children,
	markdownContext,
}: {
	children: React.ReactChild | React.ReactChild[]
	markdownContext: MarkdownContext
}): JSX.Element {
	const { View, Text } = markdownContext.ReactPdf
	const listContext = useContext(ListContext)
	const index = listContext.value.current
	listContext.value.current += 1
	return (
		<View style={markdownContext.pdfStyles.listItem}>
			<View style={{ flexGrow: 0, alignSelf: 'baseline' }}>
				<Text>
					{generatePrefix(listContext.type, listContext.level, index)}{' '}
				</Text>
			</View>
			<View style={{ flex: 1, alignSelf: 'baseline' }}>
				<CollapseText markdownContext={markdownContext}>
					{children}
				</CollapseText>
			</View>
		</View>
	)
}

/**
 * ImageWrapper - create an image element which can be displayed on a pdf
 *
 * @param {{src:string}} {src} - the url for the image
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element} - the image element to display to a pdf
 */
export function ImageWrapper({
	src,
	markdownContext,
}: {
	src: string
	markdownContext: MarkdownContext
}): JSX.Element {
	const { View, Image } = markdownContext.ReactPdf
	return (
		<View style={markdownContext.pdfStyles.imageWrapper}>
			<Image src={src} style={markdownContext.pdfStyles.image} />
		</View>
	)
}

// ---------------------------------------------------------------------- Text Collapsing ---------------------------------------------------------------

/**
 * In react-pdf, a view can not be a component of text, so we have to determine how to wrap some components based on their children. Also,
 * text is only strung together with other text within the same Text element.
 *
 * Take the markdown example `hello *world*`. This text will be translated in markdown to `<div>Hello <em>world</em></div>` which (with over rides) would result in
 * `<View>Hello <Text>world</Text></View>` which crashes `react-pdf` since `Hello` is not in a tag. Also, if I just wrap the string literals in text tags,
 * it becomes `<View><Text>Hello </Text><Text>world</Text></View>` but is rendered as
 *
 * Hello
 * *world*
 *
 * instead of the wanted "Hello world". To get the correct layout I have to translate it to
 * `<View><Text><Text>Hello </Text><Text>world</Text></Text></View>`.
 */

/**
 * areLeafsText - determine if all the leaf nodes in the JSX children graph are text nodes
 *
 * @param {React.ReactChild | React.ReactChild[]} childrenArray - the children to check
 * @param {markdownContext} markdownContext - the reactPDF and styles available to be used
 *
 * @return {boolean} - true if all leaf elements are Text nodes, false otherwise
 */
function areLeafsText(
	childrenArray: React.ReactChild[] | React.ReactFragment,
	markdownContext: MarkdownContext
): boolean {
	const children = React.Children.toArray(childrenArray) as React.ReactChild[]
	for (const child of children) {
		if (
			!child ||
			typeof child === 'string' ||
			typeof child === 'number' ||
			!Object.keys(child).length ||
			child.type === MarkdownDropdownSelectComponent
		) {
			continue
		}
		if (
			typeof child === 'function' ||
			(Array.isArray(child) && !areLeafsText(child, markdownContext))
		) {
			return false
		}
		if (
			child.type === markdownContext.ReactPdf.View ||
			child.type === markdownContext.ReactPdf.Image ||
			child.type === ViewWrapper ||
			child.type === List ||
			child.type === HeaderComponent ||
			(child.type !== markdownContext.ReactPdf.Text &&
				!(
					child.props?.children?.length &&
					areLeafsText(child.props.children, markdownContext)
				))
		) {
			return false
		}
	}
	return true
}

/**
 * _collapseText (recursive) - wrap all subsequent Text children with a Text wrapper so they act as a paragraph.
 *    Note: the results of this function are added to `putArray`, and an in progress result may be in `runningText`after function completes.
 *
 * @param {React.ReactChild | React.ReactChild[]} childArray - the children to wrap
 * @param {JSX.Element[]} putArray - [Mutates] the array to put the results in
 * @param {React.ReactChild[]} runningText - [Mutates] the current subsequent Text children
 * @param {Record<string, unknown>} props - the props to send to the wrappers
 * @param {markdownContext} markdownContext - the reactPDF and styles available to be used
 */
function _collapseText(
	childArray: React.ReactChild | React.ReactChild[],
	putArray: JSX.Element[],
	runningText: React.ReactChild[],
	props: Record<string, unknown>,
	markdownContext: MarkdownContext
) {
	React.Children.forEach(childArray, (child: React.ReactChild, index) => {
		let key = `${putArray.length}-${runningText.length}-${index}`

		if (typeof child === 'string') {
			// text gets wrapped in a react-pdf Text component when added to the putArray
			runningText.push(child)
			return
		}

		if (Array.isArray(child)) {
			// child is an array of children, check each child
			_collapseText(child, putArray, runningText, props, markdownContext)
			return
		}

		// key may have already been used, update to make sure the key is unique
		key = `${putArray.length}-${runningText.length}-${index}`

		if (areLeafsText([child], markdownContext)) {
			// keep all subsequent text leafs together
			runningText.push(child)
			return
		}

		if (runningText.length) {
			// we encountered a View type element, collapse all running text into a paragraph.
			putArray.push(
				<markdownContext.ReactPdf.Text {...props} key={key}>
					{[...runningText]}
				</markdownContext.ReactPdf.Text>
			)
			runningText.length = 0
		}

		// apply the View type child
		putArray.push(
			<markdownContext.ReactPdf.View {...props} key={`wrapper-${key}`}>
				{child}
			</markdownContext.ReactPdf.View>
		)
	})
}

/**
 * collapseText - wrap all subsequent Text children with a Text wrapper so they act as a paragraph.
 *    Kicks off a recursive tree traversal with _collapseText.
 *
 * @param {React.ReactChild | React.ReactChild[]} children - the children to wrap
 * @param {Record<string, unknown>} props - the props to send to the wrappers
 * @param {markdownContext} markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element[]} all the children wrapped correctly
 */
function collapseText(
	children: React.ReactChild | React.ReactChild[],
	props: Record<string, unknown>,
	markdownContext: MarkdownContext
): JSX.Element[] {
	const putArray: JSX.Element[] = []
	const runningText: React.ReactChild[] = []
	_collapseText(children, putArray, runningText, props, markdownContext)

	if (runningText.length) {
		putArray.push(
			<markdownContext.ReactPdf.Text {...props} key={putArray.length}>
				{runningText}
			</markdownContext.ReactPdf.Text>
		)
	}

	return putArray
}

/**
 * CollapseText - wrap all subsequent Text children with a Text wrapper so they act as a paragraph.
 *
 * @param {Props} props - the react props for the component
 * @param {React.ReactChild | React.ReactChild[]} props.children - the children to collapse
 * @param {string} props.style - the style to apply to the wrappers
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element} - an element with all the subsequent Text children acting as paragraphs
 */
export function CollapseText({
	children,
	markdownContext,
	...props
}: {
	children: React.ReactChild | React.ReactChild[]
	markdownContext: MarkdownContext
	style?: Style
}): JSX.Element | null {
	try {
		return <>{collapseText(children, props, markdownContext)}</>
	} catch (error) {
		console.error(error)
	}
	return null
}

/**
 * HeaderComponent - an override for the header tags
 *
 * @param {Props} props - the react props for the component
 * @param {React.ReactChild | React.ReactChild[]} props.children - the children of the <a> tag
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 * @param {?Style} props.style - the styles to apply to the header
 *
 * @return {JSX.Element} - an element which renders the header to a pdf
 */
export function HeaderComponent({
	children,
	markdownContext,
	style,
}: {
	children: React.ReactChild | React.ReactChild[]
	markdownContext: MarkdownContext
	style?: Style
}): JSX.Element {
	return (
		<markdownContext.ReactPdf.View>
			<CollapseText style={style} markdownContext={markdownContext}>
				{children}
			</CollapseText>
		</markdownContext.ReactPdf.View>
	)
}
